/**
 * Centralized Logging System
 *
 * Features:
 * - Multiple log levels (debug, info, warn, error)
 * - Automatic removal of debug logs in production builds
 * - TypeScript support with proper types
 * - Namespace/context support for better debugging
 * - Zero runtime overhead in production
 *
 * @example
 * ```tsx
 * // Basic usage
 * import { logger } from '@/lib/logger';
 * logger.debug('Debug message');
 * logger.info('Info message');
 * logger.warn('Warning message');
 * logger.error('Error message');
 *
 * // With namespace
 * import { createLogger } from '@/lib/logger';
 * const log = createLogger({ namespace: 'TrackTable' });
 * log.debug('Track selected:', trackId);
 * log.info('Tracks loaded:', tracks.length);
 * log.warn('Rate limit approaching');
 * log.error('Failed to load tracks:', error);
 * ```
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LoggerOptions {
  namespace?: string;
  enabled?: boolean;
}

export interface Logger {
  debug: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
}

/**
 * Create a logger instance with optional namespace
 *
 * @example
 * ```tsx
 * const log = createLogger({ namespace: 'TrackTable' });
 * log.debug('Track selected:', trackId);
 * log.info('Tracks loaded:', tracks.length);
 * log.warn('Rate limit approaching');
 * log.error('Failed to load tracks:', error);
 * ```
 */
export function createLogger(options: LoggerOptions = {}): Logger {
  const { namespace, enabled = true } = options;

  // Helper to format messages with namespace
  const formatMessage = (args: unknown[]): unknown[] => {
    if (namespace) {
      return [`[${namespace}]`, ...args];
    }
    return args;
  };

  // In production, debug logs will be stripped by Vite's dead code elimination
  const isDevelopment = import.meta.env.DEV;

  return {
    debug: (...args: unknown[]) => {
      if (isDevelopment && enabled) {
        console.log(...formatMessage(args));
      }
    },

    info: (...args: unknown[]) => {
      if (enabled) {
        console.info(...formatMessage(args));
      }
    },

    warn: (...args: unknown[]) => {
      if (enabled) {
        console.warn(...formatMessage(args));
      }
    },

    error: (...args: unknown[]) => {
      if (enabled) {
        console.error(...formatMessage(args));
      }
    },
  };
}

/**
 * Global logger instance for general use
 */
export const logger = createLogger();

/**
 * Convenience exports for direct usage
 */
export const { debug, info, warn, error } = logger;
